import * as Sentry from '@sentry/react';

type Tags = Record<string, string | number | boolean | null | undefined>;

const coerceTags = (tags?: Tags): Record<string, string> | undefined => {
  if (!tags) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(tags)) {
    if (v === undefined) continue;
    out[k] = String(v);
  }
  return out;
};

export const metrics = {
  count(name: string, value = 1, tags?: Tags) {
    const coercedTags = coerceTags(tags);

    // Metrics are not attached to error events, so also leave a breadcrumb: this makes
    // call-state transitions visible in the timeline of any error captured later.
    try {
      Sentry.addBreadcrumb({
        category: 'metric',
        level: 'info',
        message: name,
        data: { value, ...(coercedTags ?? {}) },
      });
    } catch {
      // breadcrumbs are best-effort
    }

    // @ts-ignore - metrics may not exist on older SDKs at dev time
    if ((Sentry as any).metrics?.count) {
      // @ts-ignore
      (Sentry as any).metrics.count(name, value, { tags: coercedTags });
    } else {
      if (import.meta?.env?.MODE !== 'production') {
        console.debug('[metrics.count]', name, value, tags);
      }
    }
  },
  gauge(name: string, value: number, tags?: Tags) {
    // @ts-ignore
    if ((Sentry as any).metrics?.gauge) {
      // @ts-ignore
      (Sentry as any).metrics.gauge(name, value, { tags: coerceTags(tags) });
    } else {
      if (import.meta?.env?.MODE !== 'production') {
        console.debug('[metrics.gauge]', name, value, tags);
      }
    }
  },
  distribution(name: string, value: number, tags?: Tags) {
    // @ts-ignore
    if ((Sentry as any).metrics?.distribution) {
      // @ts-ignore
      (Sentry as any).metrics.distribution(name, value, {
        unit: 'millisecond',
        tags: coerceTags(tags),
      });
    } else {
      if (import.meta?.env?.MODE !== 'production') {
        console.debug('[metrics.dist]', name, value, tags);
      }
    }
  },
};

// Simple timer registry for cross-module start/stop use
const timers = new Map<string, number>();

export const startTimer = (key: string) => {
  timers.set(key, performance.now());
};

export const endTimer = (key: string, metricName: string, tags?: Tags) => {
  const start = timers.get(key);
  if (start !== undefined) {
    const ms = performance.now() - start;
    metrics.distribution(metricName, ms, tags);
    timers.delete(key);
    // console.log(`[METRIC SENT] ${metricName}: ${Math.round(ms)}ms`, tags);
    return ms;
  }
  // console.warn(`[METRIC SKIPPED] No startTimer() found for key: ${key}`);
  return undefined;
};

// Convenience helpers for consistent keys
export const mediaKey = (uri: string) => `media:${uri}`;
export const taskKey = () => 'task:start';
export const callKey = () => 'call:start';
